import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { logout, tokenReceived } from '../features/auth/authSlice';
import { settings } from '../settings';
import { RootState } from '../redux/store';
import {
    Destino,
    DestinosResponse,
    Factura,
    FacturaDetalle,
    FacturaPayload,
    FacturasResponse,
    MotivosDebitoResponse,
    ParametricosResponse,
    PrestadoresResponse
} from '../pages/AuditoriaFacturacion/types';

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result: any = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        const { auth }: any = api.getState();

        const refreshResult = await baseQuery(
            {
                url: '/auth/refresh-token',
                method: 'POST',
                credentials: 'include',
                body: { username: auth?.user?.usuario }
            },
            api,
            extraOptions
        );
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

// Los destinos no traen un id propio: se arma uno sintético (tipo + id del organismo o contrato)
// para que el Autocomplete pueda comparar opciones y usarlo como key.
export const getDestinoId = (destino: Omit<Destino, 'id'>) =>
    `${destino.tipo}-${destino.organismo?.id ?? destino.contrato?.id}`;

export const auditoriaFacturacionApi = createApi({
    reducerPath: 'AuditoriaFacturacion',
    tagTypes: ['Facturas'],
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        getParametricos: builder.query<ParametricosResponse, void>({
            query: () => ({
                url: `auditoria-facturacion/parametricos`
            })
        }),
        // Motivos de débito activos y ordenados. Es un paramétrico: se cachea una hora.
        getMotivosDebito: builder.query<MotivosDebitoResponse, void>({
            query: () => ({
                url: `auditoria-facturacion/motivos-debito`
            }),
            keepUnusedDataFor: 3600
        }),
        getPrestadores: builder.query<
            PrestadoresResponse,
            { q?: string; skip?: number; take?: number }
        >({
            query: (params) => ({
                url: `auditoria-facturacion/prestadores`,
                params
            })
        }),
        getDestinos: builder.query<
            DestinosResponse,
            { q: string; idOrganizacion?: number | null; take?: number }
        >({
            query: ({ q, idOrganizacion, take = 20 }) => ({
                url: `auditoria-facturacion/destinos`,
                params: {
                    q,
                    take,
                    ...(idOrganizacion ? { idOrganizacion } : {})
                }
            }),
            transformResponse: (response: { data: Omit<Destino, 'id'>[] }) => ({
                data: (response?.data || []).map((destino) => ({
                    ...destino,
                    id: getDestinoId(destino)
                }))
            })
        }),
        getFacturas: builder.query<FacturasResponse, Record<string, any>>({
            query: (params) => ({
                url: `auditoria-facturacion/facturas`,
                params
            }),
            providesTags: ['Facturas']
        }),
        getFactura: builder.query<{ data: FacturaDetalle }, number>({
            query: (id) => ({
                url: `auditoria-facturacion/facturas/${id}`
            }),
            providesTags: (_result, _error, id) => [{ type: 'Facturas', id }]
        }),
        createFactura: builder.mutation<{ data: { id: number } }, { body: FacturaPayload }>({
            query: ({ body }) => ({
                url: `auditoria-facturacion/facturas`,
                method: 'POST',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Facturas'])
        }),
        updateFactura: builder.mutation<
            { data: Factura },
            { id: number; body: FacturaPayload }
        >({
            query: ({ id, body }) => ({
                url: `auditoria-facturacion/facturas/${id}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: (_, error, { id }) =>
                error ? [] : ['Facturas', { type: 'Facturas', id }]
        }),
        anularFactura: builder.mutation<{ data: Factura }, { id: number; motivo: string }>({
            query: ({ id, motivo }) => ({
                url: `auditoria-facturacion/facturas/${id}/anulacion`,
                method: 'PATCH',
                body: { motivo }
            }),
            invalidatesTags: (_, error, { id }) =>
                error ? [] : ['Facturas', { type: 'Facturas', id }]
        })
    })
});

export const {
    useGetParametricosQuery,
    useGetMotivosDebitoQuery,
    useGetPrestadoresQuery,
    useGetDestinosQuery,
    useGetFacturasQuery,
    useGetFacturaQuery,
    useCreateFacturaMutation,
    useUpdateFacturaMutation,
    useAnularFacturaMutation
} = auditoriaFacturacionApi;
